/** biome-ignore-all lint/style/noMagicNumbers: TODO: fix */
import ExcelJs from 'exceljs';
import i18n from 'i18next';

import { getDateLabel, hyphenOneToEmptyInObject } from '@/utilz';
import { SHIPMENT_RATE_SHOP_RESULTS_EXPORT_COLUMNS } from '@/contants/ShipmentEnums';

import { getLocalDateTime } from '../date';
import { generateExcelColumnNames, writeFileAndDownload } from './common';

export const exportSaversDataToExcel = (data, fileName, columns, sheetName = 'Sheet1') => {
  const workbook = new ExcelJs.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);
  const headers = columns.map((column) => column.text);
  worksheet.addRow(headers);

  data.forEach((item) => {
    const rowData = columns.map((column) => item[column.dataField]);
    worksheet.addRow(rowData);
  });

  workbook.xlsx.writeBuffer().then((buffer) => {
    writeFileAndDownload(fileName, buffer);
  });
};

export const exportRateShopDetailsToExcel = (rateShops) => {
  const sheetName = `RateShop ${i18n.t('translation:visibility.detailCard.detailsTab.title')}`;
  const workbook = new ExcelJs.Workbook();
  const sheet = workbook.addWorksheet(sheetName, {});

  const rows = [];
  rateShops.forEach((rateShopRow) => {
    const row = hyphenOneToEmptyInObject(rateShopRow);
    const arr = [];
    arr.push(row.rateShopKey);
    arr.push(row.carrierName);
    arr.push(row.convertedCost);
    arr.push(row.convertedCurrencyCode);
    arr.push(row.pricingBasis);
    arr.push(row.standardDaysTransit);
    arr.push(row.cargoType);
    arr.push(row.serviceDesc);
    arr.push(row.trailerkey);
    arr.push(row.equipmentTypeDetail);
    arr.push(row.userCreatedBy);
    arr.push(getDateLabel(row.createdOn));
    arr.push(row.effectiveTo);
    arr.push(row.bidComment);
    arr.push(row.spotQuoteNumber);
    rows.push(arr);
  });
  sheet.addTable({
    autoFilterEnabled: true,
    columns: SHIPMENT_RATE_SHOP_RESULTS_EXPORT_COLUMNS,
    headerRow: true,
    name: 'Header',
    ref: 'A1',
    rows,
    style: {
      showRowStripes: false,
      theme: '',
    },
    totalsRow: false,
  });
  sheet.autoFilter = 'A1:O1';
  const columnsList = generateExcelColumnNames(SHIPMENT_RATE_SHOP_RESULTS_EXPORT_COLUMNS.length);
  columnsList.forEach((column) => {
    const cell = sheet.getCell(`${column}1`);
    cell.fill = {
      fgColor: { argb: '7A7A7A' },
      pattern: 'solid',
      type: 'pattern',
    };
    cell.font = {
      bold: true,
      color: { argb: 'FFFFFF' },
      size: 10,
    };
  });

  sheet.columns = sheet.columns.map((e) => {
    const expr = e.values?.[1];
    switch (expr) {
      case 'RateShopKey':
        return { width: 20 };
      case 'Transportation Provider':
        return { width: 40 };
      case 'Converted Cost':
        return { width: 15 };
      case 'Converted Currency Code':
        return { width: 15 };
      case 'Pricing Basis':
        return { width: 30 };
      case 'Standard Transit Days':
        return { width: 30 };
      case 'Cargo Type':
        return { width: 25 };
      case 'Service Description':
        return { width: 30 };
      case 'Equipment Type':
        return { width: 20 };
      case 'Equipment Type Detail':
        return { width: 30 };
      case 'User Submitted':
        return { width: 20 };
      case i18n.t('translation:default.fields.createdOn.title'):
        return { width: 20 };
      case 'Bid Expiry Date':
        return { width: 20 };
      case 'Bid Comment':
        return { width: 30 };
      case 'Spot Quote Number':
        return { width: 30 };
      default:
        return { width: 20 };
    }
  });

  workbook.xlsx.writeBuffer().then((buffer) => {
    writeFileAndDownload(sheetName, buffer);
  });
};

export const exportShipmentListToExcel = (columns, rows, sheetNameValue, fileName) => {
  const sheetName = sheetNameValue || 'Shipment Search';
  const workbook = new ExcelJs.Workbook();
  const sheet = workbook.addWorksheet(sheetName, {});

  const { expShipDate, expDeliveryDate, shipmentDate } = columns.reduce(
    (acc, obj, index) => {
      if (obj.name === 'Expected Ship Date') {
        acc.expShipDate = index;
      } else if (obj.name === 'Expected Delivery Date') {
        acc.expDeliveryDate = index;
      } else if (obj.name === 'Shipment Date') {
        acc.shipmentDate = index;
      }
      return acc;
    },
    { expDeliveryDate: -1, expShipDate: -1, shipmentDate: -1 },
  );

  sheet.addTable({
    autoFilterEnabled: true,
    columns,
    headerRow: true,
    name: 'Header',
    ref: 'A1',
    rows,
    style: {
      showRowStripes: false,
      theme: '',
    },
    totalsRow: false,
  });
  sheet.views = [
    {
      activeCell: 'A1',
      state: 'frozen',
      xSplit: 1,
    },
  ];

  const columnsList = generateExcelColumnNames(columns.length);
  columnsList.forEach((column) => {
    const cell = sheet.getCell(`${column}1`);
    cell.fill = {
      fgColor: { argb: '75A72E' },
      pattern: 'solid',
      type: 'pattern',
    };
    cell.font = {
      bold: 700,
      color: { argb: 'FFFFFF' },
      size: 10,
    };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  });
  sheet.columns = sheet.columns.map((_e) => ({ width: 30 }));
  let rowIndex = 0;
  rows.forEach(() => {
    columnsList.forEach((column, columnIndex) => {
      const cell = sheet.getCell(`${column}${rowIndex + 2}`);
      if (columnIndex === expShipDate || columnIndex === expDeliveryDate || columnIndex === shipmentDate) {
        cell.numFmt = 'mmm dd, yyyy';
      }
    });
    rowIndex++;
  });

  if (fileName) {
    workbook.xlsx.writeBuffer().then((buffer) => {
      writeFileAndDownload(`${fileName} ${getLocalDateTime().toLocaleDateString()}`, buffer);
    });
  } else {
    workbook.xlsx.writeBuffer().then((buffer) => {
      writeFileAndDownload(`Shipment Search ${getLocalDateTime().toLocaleDateString()}`, buffer);
    });
  }
};

export const exportPurchaseOrderToExcel = (columns, rows) => {
  const sheetName = 'Purchase Orders';
  const workbook = new ExcelJs.Workbook();
  const sheet = workbook.addWorksheet(sheetName, {});
  const { orderShipDateIndex, deliveryDateIndex, createdOnIndex } = columns.reduce(
    (acc, obj, index) => {
      if (obj.key === 'OrderShipDate') {
        acc.orderShipDateIndex = index;
      } else if (obj.key === 'DeliveryDate') {
        acc.deliveryDateIndex = index;
      } else if (obj.key === 'CreatedOn') {
        acc.createdOnIndex = index;
      }
      return acc;
    },
    { createdOnIndex: -1, deliveryDateIndex: -1, orderShipDateIndex: -1 },
  );

  sheet.addTable({
    autoFilterEnabled: true,
    columns,
    headerRow: true,
    name: 'Header',
    ref: 'A1',
    rows,
    style: {
      showRowStripes: false,
      theme: '',
    },
    totalsRow: false,
  });

  sheet.views = [
    {
      activeCell: 'A1',
      state: 'frozen',
      xSplit: 1,
    },
  ];
  const columnsList = generateExcelColumnNames(columns.length);

  columnsList.forEach((column) => {
    const cell = sheet.getCell(`${column}1`);
    cell.fill = {
      fgColor: { argb: '75A72E' },
      pattern: 'solid',
      type: 'pattern',
    };
    cell.font = {
      bold: 700,
      color: { argb: 'FFFFFF' },
      size: 10,
    };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  });
  sheet.columns = sheet.columns.map((_e) => ({ width: 30 }));
  let rowIndex = 0;
  rows.forEach(() => {
    columnsList.forEach((column, columnIndex) => {
      const cell = sheet.getCell(`${column}${rowIndex + 2}`);
      if (columnIndex === orderShipDateIndex || columnIndex === deliveryDateIndex || columnIndex === createdOnIndex) {
        cell.numFmt = 'mmm dd, yyyy';
      }
    });
    rowIndex++;
  });

  workbook.xlsx.writeBuffer().then((buffer) => {
    writeFileAndDownload(`Purchase_Order_List ${getLocalDateTime().toLocaleDateString()}`, buffer);
  });
};

export const exportCreateShipmentDetailsToExcel = (shipmentItems, formData) => {
  const sheetName = `Create Shipment Item ${i18n.t('translation:visibility.detailCard.detailsTab.title')}`;
  const workbook = new ExcelJs.Workbook();
  const sheet = workbook.addWorksheet(sheetName, {});

  const rows = [];
  shipmentItems.forEach((row, index) => {
    const arr = [];
    const quantityShipped = `PoQuantityShipped${index}`;
    const countryOfOrigin = `countryOfOrigin${index}`;
    const eccn = `eccn${index}`;
    const htcCode = `htcCode${index}`;
    arr.push(row.poNumber);
    arr.push(row?.partNumber || row?.nmfcItemNumber || row?.nmfcNumber);
    arr.push(row.lineNum);
    arr.push(row.poQuantity);
    arr.push(row.poQuantityLeft);
    arr.push(row.unitPrice);
    arr.push(formData[quantityShipped]);
    arr.push(row.expShipDate);
    arr.push(row.crateNo);
    arr.push(row.productDesc);
    arr.push(formData[htcCode]);
    arr.push(formData[eccn]);
    arr.push(formData[countryOfOrigin]);
    rows.push(arr);
  });

  const columns = [
    {
      label: i18n.t('translation:default.fields.orderNumber'),
      name: 'Order Number',
    },
    {
      label: i18n.t('translation:default.fields.partNumber'),
      name: 'Part Number',
    },
    {
      label: i18n.t('translation:default.fields.lineNumber.label'),
      name: 'line Number',
    },
    {
      label: i18n.t('translation:default.fields.quantity.label'),
      name: 'Quantity',
    },
    {
      label: i18n.t('translation:default.fields.quantityLeft'),
      name: 'Quantity Left',
    },
    { label: i18n.t('translation:default.fields.price'), name: 'Price' },
    {
      label: i18n.t('translation:default.fields.quantityShipped'),
      name: 'Quantity Shipped',
    },
    {
      label: i18n.t('translation:default.fields.newShipDate'),
      name: 'New Ship Date',
    },
    {
      label: i18n.t('translation:default.fields.packageID.label'),
      name: 'Package Id',
    },
    {
      label: i18n.t('translation:default.fields.description'),
      name: 'Description',
    },
    { label: i18n.t('translation:default.fields.hts'), name: 'HTS' },
    { label: i18n.t('translation:default.fields.eccn'), name: 'ECCN' },
    {
      label: i18n.t('translation:default.fields.countryOfOrigin'),
      name: 'Country Of Origin',
    },
  ];

  const formattedColumns = columns.map(({ label }) => ({
    name: label || '---',
  }));

  sheet.addTable({
    autoFilterEnabled: true,
    columns: formattedColumns,
    headerRow: true,
    name: 'Header',
    ref: 'A1',
    rows,
    style: {
      showRowStripes: false,
      theme: '',
    },
    totalsRow: false,
  });
  sheet.autoFilter = 'A1:O1';
  const columnsList = generateExcelColumnNames(columns.length);

  columnsList.forEach((column) => {
    const cell = sheet.getCell(`${column}1`);
    cell.fill = {
      fgColor: { argb: '7A7A7A' },
      pattern: 'solid',
      type: 'pattern',
    };
    cell.font = {
      bold: 700,
      color: { argb: 'FFFFFF' },
      size: 10,
    };
  });

  sheet.columns = sheet.columns.map((_e) => {
    return { width: 20 };
  });

  workbook.xlsx.writeBuffer().then((buffer) => {
    writeFileAndDownload(sheetName, buffer);
  });
};

export const exportShipmentNotificationDistributorsListToExcel = (
  columns,
  rows,
  sheetName = 'Shipment Notification Distributors',
) => {
  const workbook = new ExcelJs.Workbook();
  const sheet = workbook.addWorksheet(sheetName, {});
  sheet.addTable({
    autoFilterEnabled: true,
    columns,
    headerRow: true,
    name: 'Header',
    ref: 'A1',
    rows,
    style: {
      showRowStripes: false,
      theme: '',
    },
    totalsRow: false,
  });
  sheet.views = [
    {
      activeCell: 'A1',
      state: 'frozen',
      xSplit: 1,
    },
  ];
  const columnsList = generateExcelColumnNames(columns.length);
  columnsList.forEach((column) => {
    const cell = sheet.getCell(`${column}1`);
    cell.fill = {
      fgColor: { argb: '7A7A7A' },
      pattern: 'solid',
      type: 'pattern',
    };
    cell.font = {
      bold: 700,
      color: { argb: 'FFFFFF' },
      size: 10,
    };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  });

  sheet.columns = sheet.columns.map((e) => {
    const expr = e.values[1];
    switch (expr) {
      case 'DistributionId':
        return { width: 20 };
      case 'Distribution Name':
        return { width: 35 };
      case 'Description':
        return { width: 50 };
      case 'Status':
        return { width: 20 };

      default:
        return { width: 20 };
    }
  });

  workbook.xlsx.writeBuffer().then((buffer) => {
    writeFileAndDownload('DistributionList', buffer);
  });
};

export const exportShipmentNotificationRoutingListToExcel = (
  columns,
  rows,
  sheetName = 'Shipment Notification Routings',
) => {
  const workbook = new ExcelJs.Workbook();
  const sheet = workbook.addWorksheet(sheetName, {});
  sheet.addTable({
    autoFilterEnabled: true,
    columns,
    headerRow: true,
    name: 'Header',
    ref: 'A1',
    rows,
    style: {
      showRowStripes: false,
      theme: '',
    },
    totalsRow: false,
  });
  sheet.views = [
    {
      activeCell: 'A1',
      state: 'frozen',
      xSplit: 1,
    },
  ];

  const columnsList = generateExcelColumnNames(columns.length);
  columnsList.forEach((column) => {
    const cell = sheet.getCell(`${column}1`);
    cell.fill = {
      fgColor: { argb: '7A7A7A' },
      pattern: 'solid',
      type: 'pattern',
    };
    cell.font = {
      bold: 700,
      color: { argb: 'FFFFFF' },
      size: 10,
    };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  });

  sheet.columns = sheet.columns.map((e) => {
    const expr = e.values[1];
    switch (expr) {
      case 'RoutingDetailId':
        return { width: 20 };
      case 'Distribution Name':
      case 'Customer Name':
      case 'User Name':
      case 'Transportation Provider':
        return { width: 35 };
      case 'Email List':
        return { width: 30 };
      case 'Status':
        return { width: 20 };

      default:
        return { width: 20 };
    }
  });

  workbook.xlsx.writeBuffer().then((buffer) => {
    writeFileAndDownload('NotificationList', buffer);
  });
};

export const exportOverChargeClaimsToExcel = (columnList, data) =>
  new Promise((resolve, reject) => {
    try {
      {
        const sheetName = 'OverChargeClaim_Search_Result';
        const workbook = new ExcelJs.Workbook();
        const sheet = workbook.addWorksheet(sheetName, {});

        const columns = columnList.map(({ columnName }) => ({
          name: columnName,
        }));

        const rows = [];
        data.forEach((row) => {
          // biome-ignore lint/style/noParameterAssign: disabled
          row = hyphenOneToEmptyInObject(row);
          const arr = [];
          columnList.forEach(({ value }) => {
            arr.push(row[value]);
          });

          rows.push(arr);
        });
        sheet.addTable({
          columns,
          headerRow: true,
          name: 'Header',
          ref: 'A1',
          rows,
          style: {
            showRowStripes: false,
            theme: '',
          },
          totalsRow: false,
        });

        sheet.columns = sheet.columns.map((e) => {
          const expr = e.values[1];
          switch (expr) {
            case 'TransportationProvider':
              return { width: 40 };
            default:
              return { width: 20 };
          }
        });

        workbook.xlsx.writeBuffer().then((buffer) => {
          writeFileAndDownload(sheetName, buffer);
          resolve(true);
        });
      }
    } catch (error) {
      reject(error);
    }
  });

export const exportLossAndDamageClaimsToExcel = (columnList, data) =>
  new Promise((resolve, reject) => {
    try {
      const sheetName = 'LossAndDamageClaim_Search_Result';
      const workbook = new ExcelJs.Workbook();
      const sheet = workbook.addWorksheet(sheetName, {});

      const columns = columnList;
      sheet.addTable({
        columns,
        headerRow: true,
        name: 'Header',
        ref: 'A1',
        rows: data,
        style: {
          showRowStripes: false,
          theme: '',
        },
        totalsRow: false,
      });

      sheet.columns = sheet.columns.map((e) => {
        const expr = e.values[1];
        switch (expr) {
          case 'Transportation Provider':
            return { width: 40 };
          default:
            return { width: 20 };
        }
      });

      workbook.xlsx.writeBuffer().then((buffer) => {
        writeFileAndDownload(sheetName, buffer);
        resolve(true);
      });
    } catch (error) {
      reject(error);
    }
  });

export const exportQuoteHistoryDetailsToExcel = (rateShops) => {
  const sheetName = 'QuoteHistory Details';
  const workbook = new ExcelJs.Workbook();
  const sheet = workbook.addWorksheet(sheetName);

  // Define columns
  const columns = [
    { header: 'Quote #', key: 'quoteNumber', width: 20 },
    { header: 'Modified By', key: 'modifiedBy', width: 50 },
    { header: 'Modified On', key: 'modifiedOn', width: 50 },
    { header: 'Tracking Date Time', key: 'trackingDatetime', width: 50 },
    { header: 'Status', key: 'status', width: 50 },
    { header: 'Awarded Notes', key: 'awardedNotes', width: 50 },
    { header: 'Cancellation Reason', key: 'cancellationReason', width: 50 },
  ];
  sheet.columns = columns;

  // Process and add rows
  rateShops.forEach((row) => {
    const processedRow = hyphenOneToEmptyInObject(row);
    sheet.addRow({
      awardedNotes: processedRow.awardedNotes,
      cancellationReason: processedRow.cancellationReason,
      modifiedBy: processedRow.modifiedBy,
      modifiedOn: processedRow.modifiedOn,
      quoteNumber: processedRow.quoteNumber,
      status: processedRow.status,
      trackingDatetime: processedRow.trackingDatetime,
    });
  });

  // Style header row
  sheet.getRow(1).eachCell((cell) => {
    cell.fill = {
      fgColor: { argb: '7A7A7A' },
      pattern: 'solid',
      type: 'pattern',
    };
    cell.font = {
      bold: true,
      color: { argb: 'FFFFFF' },
      size: 10,
    };
  });

  // Enable auto filter
  sheet.autoFilter = {
    from: 'A1',
    to: 'E1',
  };

  // Generate and download the Excel file
  workbook.xlsx.writeBuffer().then((buffer) => {
    writeFileAndDownload(`${sheetName} ${getLocalDateTime().toLocaleDateString()}`, buffer);
  });
};

export const exportRoutingGuidesToExcel = (selectedRoutingGuides) => {
  // formula to convert pixels amount into width in Calibri latter measure
  const pixelsToCalibriMeasure = (pixels) => (pixels - 5) / 7;

  const applyHeaderCellsStyle = (sheet) => {
    sheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Calibri' };
      cell.fill = {
        fgColor: { argb: 'FFFF0000' },
        pattern: 'solid',
        type: 'pattern',
      };
    });
  };

  const sheetName = 'RoutingGuides ';
  const workbook = new ExcelJs.Workbook();

  const RGSheet = workbook.addWorksheet('RG');
  RGSheet.columns = [
    { header: 'Routing Guide Name', key: 'name', width: pixelsToCalibriMeasure(210) },
    { header: 'Country', key: 'country', width: pixelsToCalibriMeasure(115) },
    { header: 'Transportation Mode', key: 'transportationMode', width: pixelsToCalibriMeasure(175) },
    { header: 'Transportation Group', key: 'transportationGroup', width: pixelsToCalibriMeasure(175) },
  ];

  // Sheet: RG
  RGSheet.addRows(
    selectedRoutingGuides.map((value) => {
      const rg = value.routingHeader;

      return {
        country: rg.country || '-',
        name: rg.name,
        transportationGroup: rg.transportationGroup || '-',
        transportationMode: rg.transportationMode || '-',
      };
    }),
  );
  applyHeaderCellsStyle(RGSheet);

  // Sheet: Approvers
  const approversSheet = workbook.addWorksheet('Approvers');
  approversSheet.columns = [
    { header: 'Routing Guide Name', key: 'rgName', width: pixelsToCalibriMeasure(210) },
    { header: 'Collaboratots', key: 'name', width: pixelsToCalibriMeasure(190) },
    { header: 'Email', key: 'email', width: pixelsToCalibriMeasure(290) },
    { header: 'Type of Access', key: 'role', width: pixelsToCalibriMeasure(150) },
  ];

  approversSheet.addRows(
    selectedRoutingGuides.reduce((acc, value) => {
      const rg = value.routingHeader;

      const collaborators = value.collaborators.map((value) => ({
        email: value.email || '-',
        name: value.name || '-',
        rgName: rg.name,
        role: value.role || '-',
      }));
      acc.push(...collaborators);

      return acc;
    }, []),
  );
  applyHeaderCellsStyle(approversSheet);

  // Sheet: Providers
  const providersSheet = workbook.addWorksheet('Providers');
  providersSheet.columns = [
    { header: 'Routing Guide Name', key: 'rgName', width: pixelsToCalibriMeasure(210) },
    { header: 'SAP Vendor ID', key: 'sapVendorId', width: pixelsToCalibriMeasure(110) },
    { header: 'Transportation Providers', key: 'transportationProvider', width: pixelsToCalibriMeasure(300) },
    { header: 'Provider SCAC', key: 'scac', width: pixelsToCalibriMeasure(110) },
    { header: 'Provider E-mail', key: 'email', width: pixelsToCalibriMeasure(200) },
  ];

  providersSheet.addRows(
    selectedRoutingGuides.reduce((acc, value) => {
      const rg = value.routingHeader;

      const carriers = value.carriers.map((value) => ({
        email: value.email || '-',
        rgName: rg.name,
        // TODO: change if field will in request
        sapVendorId: '-',
        scac: value.scac || '-',
        transportationProvider: value.transportationProvider || '-',
      }));
      acc.push(...carriers);

      return acc;
    }, []),
  );
  applyHeaderCellsStyle(providersSheet);

  workbook.xlsx.writeBuffer().then((buffer) => {
    writeFileAndDownload(`${sheetName} ${getLocalDateTime().toLocaleDateString()}`, buffer);
  });
};
